import { combineReducers } from 'redux'
import { Reducer } from 'interfaces'
import { createTimeframedState } from 'reducers/globalReducer'
import {
  PeergroupRelationCollection,
  PeergroupsData,
  PeergroupAccountBreakdownState,
  PeergroupCollection,
  PeergroupsAccountsTrendsState,
  PeergroupsTotalsState,
  PeergroupsAccountsData,
  PeergroupRelation,
  PeergroupsTotals,
  PeergroupAccountsBreakdown,
  MoneyflowPeergroupBreakdownItem,
} from 'interfaces/peergroups'
import {
  APPEND_PEER_GROUP_RELATION,
  SET_PEER_GROUPS,
  UPDATE_PEER_GROUP_RELATION,
  SET_RANDOM_RELATION,
  DELETE_RELATION,
  APPEND_PEER_GROUPS_TOTALS,
} from 'actions/peergroupActions'
import { SET_PEER_GROUP_ACCOUNT_BREAKDOWN, SET_PEER_GROUP_ACCOUNT_TRENDS } from 'actions/accountsActions'
import { APPEND_PEER_GROUP_GENERIC_TRENDS } from 'actions/genericActions'
import { EMPTY_COLLECTION } from 'helpers/normalizeHelpers'
import { AccountType } from 'interfaces/accounts'
import { SLUGS } from 'constants/peergroups'
import { extractRelation } from 'helpers/peergroupsHelpers'
import { setItemInCollection } from 'helpers/normalizeHelpers'
import { APPEND_PEER_GROUP_MONEYFLOW_TRENDS, APPEND_PEER_GROUP_MONEYFLOW_BREAKDOWN } from 'actions/moneyflowActions'
import { FullMoneyflowType } from 'interfaces/moneyflow'
import { MoneyflowPeergroupData, MoneyflowPeergroupBreakdownCollection } from 'interfaces/peergroups'
import { CreditPeergroupData } from 'interfaces/credit'
import { NetworthPeergroupData } from 'interfaces/networth'
import { Timeframe } from 'interfaces/chart'
import { concatCollections, removeByKeyFromCollection } from 'helpers/normalizeHelpers'
import { getMemberRelation } from 'helpers/peergroupsHelpers'
import { FORM_SUBMITTED_SUCCESSFULLY } from 'actions/formActions'
import { FORM_MEMBER_PROFILE } from 'constants/forms'
import { PeergroupTrendsByCategory, PeergroupTrendCollection, PeergroupTrend } from 'interfaces/trend'
import { GenericTrendType } from 'interfaces/pages'
import { needDiscardData, PEERGROUPS_DISCARD_TRIGGERS, PEERGROUPS_TOTALS_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'

const shouldDiscardPeergroups = needDiscardData(PEERGROUPS_DISCARD_TRIGGERS)

const yourPeergroupIdReducer: Reducer<number | null> = (state = null, action) => {
  if (shouldDiscardPeergroups(action)) {
    return null
  }

  switch (action.type) {
    case SET_PEER_GROUPS: {
      const { relations } = action.payload
      const yourRelation = getMemberRelation(relations)
      const yourPeergroupId = yourRelation
        ? relations.byKey[yourRelation.id].peergroupId
        : null

      return yourPeergroupId
    }

    case FORM_SUBMITTED_SUCCESSFULLY:
      return action.payload.formName === FORM_MEMBER_PROFILE
        ? null
        : state

    default:
      return state
  }
}

const yourRelationIdReducer: Reducer<number | null> = (state = null, action) => {
  if (shouldDiscardPeergroups(action)) {
    return null
  }

  switch (action.type) {
    case SET_PEER_GROUPS: {
      const { byKey, allKeys } = action.payload.relations

      const yourRelationId = allKeys.find((id) =>
        byKey[id].slug === SLUGS.peers) || null

      return yourRelationId
    }

    case FORM_SUBMITTED_SUCCESSFULLY:
      return action.payload.formName === FORM_MEMBER_PROFILE
        ? null
        : state

    default:
      return state
  }
}

const relationsReducer: Reducer<PeergroupRelationCollection> = (state = EMPTY_COLLECTION, action) => {
  if (shouldDiscardPeergroups(action)) {
    return EMPTY_COLLECTION
  }

  switch (action.type) {
    case SET_PEER_GROUPS:
      return action.payload.relations

    case SET_RANDOM_RELATION:
    case APPEND_PEER_GROUP_RELATION: {
      const relation = extractRelation(action.payload)
      return setItemInCollection(state, relation, 'id')
    }

    case UPDATE_PEER_GROUP_RELATION: {
      const relation = extractRelation(action.payload)
      const nextRelationsByKey = Object.assign({}, state.byKey, {
        [relation.id]: relation,
      })

      return Object.assign({}, state, { byKey: nextRelationsByKey })
    }

    case FORM_SUBMITTED_SUCCESSFULLY:
      return action.payload.formName === FORM_MEMBER_PROFILE
        ? EMPTY_COLLECTION
        : state

    case DELETE_RELATION:
      return removeByKeyFromCollection<PeergroupRelation, 'id'>(state, action.payload)

    default:
      return state
  }
}

const peergroupsReducer: Reducer<PeergroupCollection> = (state = EMPTY_COLLECTION, action) => {
  if (shouldDiscardPeergroups(action)) {
    return EMPTY_COLLECTION
  }

  switch (action.type) {
    case SET_PEER_GROUPS:
      return action.payload.peergroups

    case UPDATE_PEER_GROUP_RELATION:
    case APPEND_PEER_GROUP_RELATION:
    case SET_RANDOM_RELATION:
      return setItemInCollection(state, action.payload.peergroup, 'id')

    case FORM_SUBMITTED_SUCCESSFULLY:
      return action.payload.formName === FORM_MEMBER_PROFILE
        ? EMPTY_COLLECTION
        : state

    default:
      return state
  }
}

const totalsReducer: Reducer<PeergroupsTotalsState> = (state = {}, action) => {
  if (needDiscardData(PEERGROUPS_TOTALS_DISCARD_TRIGGERS)(action)) {
    return {}
  }

  switch (action.type) {
    case APPEND_PEER_GROUPS_TOTALS: {
      const { date, totals } = action.payload
      const prevTotals = state[date] || EMPTY_COLLECTION
      const nextTotals = concatCollections<PeergroupsTotals, 'id'>(prevTotals, totals)
      return {
        ...state,
        [date]: nextTotals,
      }
    }

    case FORM_SUBMITTED_SUCCESSFULLY:
      return action.payload.formName === FORM_MEMBER_PROFILE
        ? {}
        : state

    default:
      return state
  }
}


// ===========================================================================//
//                                   ACCOUNTS                                 //
// ===========================================================================//

const createTrendsReducer = (type: AccountType): Reducer<PeergroupsAccountsTrendsState> =>
  (state = {}, action) => {
    if (shouldDiscardPeergroups(action)) {
      return {}
    }

    if (action.type === SET_PEER_GROUP_ACCOUNT_TRENDS && type === action.payload.type) {
      const { category, data } = action.payload
      const prevTrends = state[category]
      const nextTrends = prevTrends ? concatCollections<PeergroupTrend, 'id'>(prevTrends, data) : data

      return {
        ...state,
        [category]: nextTrends,
      }
    }

    return state
  }

const createAccountBreakdownReducer = (accountType: AccountType): Reducer<PeergroupAccountBreakdownState> =>
  (state = {}, action) => {
    if (shouldDiscardPeergroups(action)) {
      return {}
    }

    if (action.type === SET_PEER_GROUP_ACCOUNT_BREAKDOWN && accountType === action.payload.type) {
      const { data, date } = action.payload
      return {
        ...state,
        [date]: concatCollections<PeergroupAccountsBreakdown, 'id'>(state[date] || EMPTY_COLLECTION, data),
      }
    }
    return state
  }


// ===========================================================================//
//                                 MONEY FLOW                                 //
// ===========================================================================//

const createMoneyflowBreakdownReducer = (moneyflowType: FullMoneyflowType) => (timeframe: Timeframe): Reducer<MoneyflowPeergroupBreakdownCollection> =>
  (state = EMPTY_COLLECTION, action) => {
    if (shouldDiscardPeergroups(action)) {
      return EMPTY_COLLECTION
    }

    if (action.type === APPEND_PEER_GROUP_MONEYFLOW_BREAKDOWN &&
        action.payload.type === moneyflowType &&
        action.payload.timeframe === timeframe
    ) {
      return concatCollections<MoneyflowPeergroupBreakdownItem, 'id'>(state, action.payload.data)
    }

    return state
  }

const createMoneyflowBreakdownsReducer = (moneyflowType: FullMoneyflowType) => {
  const creator = createMoneyflowBreakdownReducer(moneyflowType)

  return createTimeframedState(creator)
}

const createMoneyflowTrendReducer = (moneyflowType: FullMoneyflowType) => (timeframe: Timeframe): Reducer<PeergroupTrendsByCategory> =>
  (state = {}, action) => {
    if (shouldDiscardPeergroups(action)) {
      return {}
    }

    if (
      action.type === APPEND_PEER_GROUP_MONEYFLOW_TRENDS
      && action.payload.type === moneyflowType
      && action.payload.timeframe === timeframe
    ) {
      const { category, data } = action.payload
      const prevTrends = (state[category] || EMPTY_COLLECTION) as PeergroupTrendCollection

      return {
        ...state,
        [action.payload.category]: concatCollections<PeergroupTrend, 'id'>(prevTrends, data),
      }
    }

    return state
  }

const createMoneyflowTrendsReducer = (moneyflowType: FullMoneyflowType) => {
  const creator = createMoneyflowTrendReducer(moneyflowType)

  return createTimeframedState(creator)
}

// ===========================================================================//
//                                   GENERIC                                  //
// ===========================================================================//

const createGenericTrendReducer = (type: GenericTrendType) => (timeframe: Timeframe): Reducer<PeergroupTrendCollection> =>
  (state = EMPTY_COLLECTION, action) => {
    if (shouldDiscardPeergroups(action)) {
      return EMPTY_COLLECTION
    }

    if (
      action.type === APPEND_PEER_GROUP_GENERIC_TRENDS
      && action.payload.type === type
      && action.payload.timeframe === timeframe
    ) {
      return concatCollections<PeergroupTrend, 'id'>(state, action.payload.data)
    }

    return state
  }

const createGenericTrendsReducer = (type: GenericTrendType) => {
  const creator = createGenericTrendReducer(type)

  return createTimeframedState(creator)
}

export default combineReducers<PeergroupsData>({
  yourPeergroupId: yourPeergroupIdReducer,
  yourRelationId: yourRelationIdReducer,
  relations: relationsReducer,
  peergroups: peergroupsReducer,
  totals: totalsReducer,
  debts: combineReducers<PeergroupsAccountsData>({
    trends: createTrendsReducer('debts'),
    breakdown: createAccountBreakdownReducer('debts'),
  }),
  assets: combineReducers<PeergroupsAccountsData>({
    trends: createTrendsReducer('assets'),
    breakdown: createAccountBreakdownReducer('assets'),
  }),
  spending: combineReducers<MoneyflowPeergroupData>({
    breakdown: createMoneyflowBreakdownsReducer('spending'),
    trends: createMoneyflowTrendsReducer('spending'),
  }),
  income: combineReducers<MoneyflowPeergroupData>({
    breakdown: createMoneyflowBreakdownsReducer('income'),
    trends: createMoneyflowTrendsReducer('income'),
  }),
  transactions: combineReducers<MoneyflowPeergroupData>({
    breakdown: createMoneyflowBreakdownsReducer('transactions'),
    trends: createMoneyflowTrendsReducer('transactions'),
  }),
  creditscore: combineReducers<CreditPeergroupData>({
    trends: createGenericTrendsReducer('creditscore'),
  }),
  networth: combineReducers<NetworthPeergroupData>({
    trends: createGenericTrendsReducer('networth'),
  }),
})
